import React from 'react';
import { Download, Youtube, Calculator } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import TaxCalculator from '../components/TaxCalculator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const ResourcesPage = () => {
  // Available downloads (files that exist)
  const availableDownloads = [
    {
      title: 'Expense Tracker 2025',
      description: 'Track and organize your expenses for the 2025 tax year',
      type: 'Excel Template',
      filename: 'Expense_Tracker2025.xlsx',
      available: true,
    },
  ];

  // Coming soon resources
  const comingSoonResources = [
    {
      title: 'Personal Tax Filing Checklist 2025',
      description: 'Complete checklist of documents needed for personal tax filing',
      type: 'PDF Guide',
    },
    {
      title: 'Newcomer Tax Guide',
      description: 'Essential tax information for new immigrants and newcomers to Canada',
      type: 'PDF Guide',
    },
    {
      title: 'Student Tax Credits Worksheet',
      description: 'Track and maximize your education-related tax credits',
      type: 'Excel Template',
    },
    {
      title: 'Tax Deductions Guide',
      description: 'Common tax deductions and credits you might be missing',
      type: 'PDF Guide',
    },
    {
      title: 'Moving Expense Calculator',
      description: 'Calculate eligible moving expenses for work or school relocation',
      type: 'Excel Template',
    },
  ];

  const faqs = [
    {
      question: 'When is the deadline for filing personal taxes in Canada?',
      answer: 'The deadline for filing personal income tax returns is April 30th each year. If you are self-employed, the deadline is June 15th, but any taxes owing are still due by April 30th to avoid interest charges.',
    },
    {
      question: 'I\'m new to Canada. Do I need to file taxes?',
      answer: 'Yes, if you lived in Canada for more than 183 days in the year, you are considered a resident for tax purposes and must file a tax return. Even if you had no income, filing can help you receive benefits like GST/HST credits and Canada Child Benefit.',
    },
    {
      question: 'What documents do I need to file my taxes as a student?',
      answer: 'You\'ll need your T4 slips (if you worked), T2202 (tuition certificate), receipts for moving expenses if you moved for school, student loan interest statements, and any T4A or T5 slips for scholarships or bursaries.',
    },
    {
      question: 'Can I claim my tuition fees on my tax return?',
      answer: 'Yes! Tuition fees paid to eligible educational institutions qualify for the tuition tax credit. If you don\'t need all the credits this year, you can carry them forward to future years or transfer some to a parent or spouse.',
    },
    {
      question: 'I\'m self-employed. What expenses can I deduct?',
      answer: 'As a self-employed individual, you can deduct business-related expenses like home office costs, vehicle expenses, supplies, professional fees, advertising, and insurance. Keep all receipts and we\'ll help you maximize legitimate deductions.',
    },
    {
      question: 'How long does it take to get a tax refund?',
      answer: 'If you file electronically with direct deposit set up, the CRA typically processes returns within 2 weeks. Paper returns can take 8-12 weeks. We file all returns electronically to ensure you get your refund as quickly as possible.',
    },
    {
      question: 'What is the GST/HST credit and how do I get it?',
      answer: 'The GST/HST credit is a tax-free quarterly payment to help individuals and families with low to modest incomes offset the GST/HST they pay. You\'re automatically considered when you file your tax return - no separate application needed.',
    },
    {
      question: 'I received a notice from CRA. What should I do?',
      answer: 'Don\'t panic! CRA notices can be about reassessments, missing information, or benefit updates. Contact us immediately and we\'ll review the notice, respond on your behalf, and help resolve any issues quickly.',
    },
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-blue-50 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Resources & Tools
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Free tools, guides, and educational content to help you manage your business finances better
          </p>
        </div>
      </section>

      {/* Tax Calculator Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Calculator className="h-12 w-12 text-blue-600 mx-auto mb-4" />
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Tax Refund Calculator
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Estimate your tax refund or amount owing with our 2025 Canadian tax calculator
            </p>
          </div>
          <TaxCalculator />
        </div>
      </section>

      {/* YouTube Videos Section */}
      <section className="py-20 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Youtube className="h-12 w-12 text-blue-600 mx-auto mb-4" />
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Educational Videos
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              Learn about tax tips, bookkeeping best practices, and business financial management
            </p>
            <Button asChild variant="outline" size="lg" className="border-blue-600 text-blue-600 hover:bg-blue-50">
              <a href="https://youtube.com/@manomaytaxinc.?si=IR4t0ZifyisnfpZJ" target="_blank" rel="noopener noreferrer">
                Visit Our YouTube Channel
              </a>
            </Button>
          </div>
          
          {/* Placeholder for videos - will show when channel has content */}
          <div className="bg-white p-12 rounded-2xl shadow-lg text-center">
            <Youtube className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Videos Coming Soon!</h3>
            <p className="text-gray-600 mb-6">
              Subscribe to our YouTube channel to get notified when we publish new educational content
            </p>
            <Button asChild className="bg-red-600 hover:bg-red-700 text-white">
              <a href="https://youtube.com/@manomaytaxinc.?si=IR4t0ZifyisnfpZJ" target="_blank" rel="noopener noreferrer">
                Subscribe Now
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Downloadable Resources */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Download className="h-12 w-12 text-blue-600 mx-auto mb-4" />
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Downloadable Resources
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Free templates, checklists, and guides to help manage your business finances
            </p>
          </div>

          {/* Available Downloads */}
          {availableDownloads.length > 0 && (
            <div className="mb-12">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">Available Now</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
                {availableDownloads.map((resource, index) => (
                  <Card key={index} className="border-2 border-green-200 bg-green-50 hover:shadow-lg transition-all duration-300" data-testid={`download-card-${index}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <Download className="h-8 w-8 text-green-600" />
                        <span className="text-xs font-semibold text-green-600 bg-green-100 px-3 py-1 rounded-full">
                          {resource.type}
                        </span>
                      </div>
                      <CardTitle className="text-lg mt-4">{resource.title}</CardTitle>
                      <CardDescription>{resource.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button asChild className="w-full bg-green-600 hover:bg-green-700 text-white" data-testid={`download-btn-${index}`}>
                        <a href={`/downloads/${resource.filename}`} download>
                          <Download className="mr-2 h-4 w-4" /> Download Now
                        </a>
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Coming Soon */}
          <div>
            <h3 className="text-xl font-semibold text-gray-500 mb-6 text-center">Coming Soon</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {comingSoonResources.map((resource, index) => (
                <Card key={index} className="border-2 hover:border-blue-300 hover:shadow-lg transition-all duration-300 opacity-75">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <Download className="h-8 w-8 text-gray-400" />
                      <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                        {resource.type}
                      </span>
                    </div>
                    <CardTitle className="text-lg mt-4">{resource.title}</CardTitle>
                    <CardDescription>{resource.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" className="w-full border-gray-300 text-gray-500" disabled>
                      Coming Soon
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div className="mt-12 bg-blue-50 p-8 rounded-xl text-center">
            <p className="text-gray-700">
              <strong>Need more resources?</strong> Contact us and let us know what templates or guides would be helpful for you.
            </p>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20 bg-slate-50">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Quick answers to common questions about taxes and business finances
            </p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-white border-2 rounded-lg px-6 hover:border-blue-300 transition-colors"
              >
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-blue-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 pt-2">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Still Have Questions?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Our team is here to help. Get personalized answers to your specific financial questions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6">
              <a href="/contact">
                Contact Us
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-blue-700 text-lg px-8 py-6">
              <a href="https://wa.me/message/E7OKL6FXEAG6L1" target="_blank" rel="noopener noreferrer">
                Chat on WhatsApp
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage;
